import React, {useState} from 'react';
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import {TenantLine} from "../../Interfaces/ITenantSystem";
import {ScopeType, TenantManagementScope} from "../../Utils/tenantSystem";
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {deepCopy} from "../../../../Utils/transformer";
import {HubPathRouting} from "../../../../HubFramework/pathBuilder";
import {IAppConfig} from "../../Interfaces/IConfig";
import {IScopeOverview} from "../../Interfaces/interfaces";
import RoleSelector from "./RoleSelector";

interface AddScopeModalProps {
    onClose: () => void;
    onAddScope: (newScope: TenantLine) => void;
    initialScope?: TenantLine;
    isEditMode?: boolean;
    globalScope?: boolean;
    scopeType: ScopeType;
    appConfig?: IAppConfig;
    scopeOverviewIdms?: IScopeOverview;
    authLevel?: string
    setAuthLevel?: (authLevel: string) => void
}

/**
 * Add scope modal
 * @param isOpen
 * @param onClose
 * @param onAddScope
 * @param initialScope
 * @param isEditMode
 * @constructor
 */
const AddEditScopeModal: React.FC<AddScopeModalProps> = ({
                                                             onClose,
                                                             onAddScope,
                                                             initialScope,
                                                             isEditMode = false,
                                                             globalScope = false,
                                                             scopeType,
                                                             appConfig,
                                                             scopeOverviewIdms,
                                                             authLevel,
                                                             setAuthLevel
                                                         }) => {
    const emptyScope: TenantLine = {id: 0, scopes: ['user', ''], value: "", tmp: "", fullscope: "user"};

    let hubAuthLevels = ["blocked", "neutral", "reader", "editor", "admin"]
    if (scopeType === ScopeType.Hub && !globalScope) {
        hubAuthLevels = ["blocked", "reader", "editor", "admin"]
    }
    const authLevels = appConfig?.authLevels ? appConfig.authLevels.split(",") : hubAuthLevels;

    // Add Scope
    const defaultScope = HubPathRouting.currentProject && !HubPathRouting.currentModule ?
        {
            id: 0,
            scopes: ["user", HubPathRouting.currentProject.id],
            value: "",
            tmp: "",
            fullscope: "user/" + HubPathRouting.currentProject.id
        }
        : emptyScope;

    const [tenantLine, setTenantLine] = useState<TenantLine>(initialScope ? deepCopy(initialScope) : defaultScope);

    const {createErrorToast} = useMain();

    const checkForValidContent = () => {
        const ret = tenantLine.value !== ""
            && tenantLine.scopes.find(scope => scope === "") === undefined
            && (globalScope || tenantLine.scopes.length > 1);

        if (!ret) {
            createErrorToast("Please fill in all fields. Make sure to add at least one scope, if you define user scopes.");
        }
        return ret;
    };

    const getEditMode = () => {
        if (appConfig?.IsTenantManagementDisabled) {
            return "Edit Role"
        } else if (isEditMode) {
            return "Edit Scope"
        } else {
            return "Add Scope"
        }
    }

    const submitRole = () => {
        if (appConfig && appConfig.IsTenantManagementDisabled) {
            // check for valid role
            if (authLevel.startsWith("{") || authLevel === "") {
                createErrorToast("Please select a valid role");
                return;
            }

            setAuthLevel(authLevel)
        } else {
            checkForValidContent() && onAddScope(tenantLine)
        }
    }

    const scopeNamespace = scopeType === ScopeType.Hub ? 'hub' : 'idms';
    const modeLabel = appConfig?.IsTenantManagementDisabled ? 'role' : (isEditMode ? 'edit' : 'add');
    const scopeLabel = globalScope ? 'global' : 'module';
    const baseId = `${scopeNamespace}-scope-modal-${modeLabel}-${scopeLabel}`;

    return (
        <>
            <OdxModal
                size="small"
                title={getEditMode()}
                isOpen={true}
                variant="sidesheet"
                footerButtons={[
                    <odx-button
                        key="close"
                        id={`${baseId}-close-btn`}
                        variant="neutral"
                        size="lg"
                        type="button"
                        onClick={onClose}
                    >
                        Close
                    </odx-button>,
                    <odx-button
                        key="submit"
                        id={`${baseId}-submit-btn`}
                        variant="primary"
                        size="lg"
                        type="button"
                        onClick={() => {
                            submitRole()
                        }}
                    >
                        {isEditMode ? "Save" : "Add"}
                    </odx-button>
                ]}
                onBackButtonClick={onClose}
                id={baseId}
            >
                {
                    appConfig?.IsTenantManagementDisabled ?
                        <RoleSelector authLevels={authLevels}
                                      selectedRole={authLevel}
                                      onRoleChange={(role) => setAuthLevel(role)}
                                      id={`${baseId}-role-selector`}
                        />
                        :
                        <TenantManagementScope
                            setTenantSystemFunctionProps={setTenantLine}
                            tenantSystemProps={tenantLine}
                            global={true}
                            authLevels={authLevels}
                            listOfIDMSSubscopes={scopeOverviewIdms ? scopeOverviewIdms.subscopelist : []}
                            projectId={"null"}
                            useSelectOptions={true}
                            showAddScopeButton={!globalScope}
                            scopeType={scopeType}
                        />
                }

            </OdxModal>
        </>
    );
};

export default AddEditScopeModal;
